import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Plus,
  Pencil,
  Trash2,
  XCircle,
  Search,
} from 'lucide-react';
import {
  Card,
  CardContent,
  Button,
  Input,
  Select,
  Modal,
  LoadingSpinner,
} from '../components/ui';
import {
  getLedger,
  getUsers,
  getSettings,
  addSubscription,
  updateSubscription,
  endSubscription,
  deleteSubscription,
} from '../services/api';
import type { Subscription, SubscriptionFormData, User, Settings } from '../types';
import { useAuth } from '../contexts/AuthContext';

const getInitialFormData = (currency = 'INR'): SubscriptionFormData => ({
  name: '',
  amount: 0,
  currency,
  billingDay: 1,
  billingCycle: 'monthly',
  startDate: format(new Date(), 'yyyy-MM-dd'),
  category: '',
  notes: '',
  ownerUserId: '',
});

const currencyOptionsBase = [
  { value: 'INR', label: 'INR (₹)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'JPY', label: 'JPY (¥)' },
  { value: 'AED', label: 'AED (د.إ)' },
];

export default function Subscriptions() {
  const { user } = useAuth();
  const isRootAdmin = Boolean(user?.isRootAdmin);
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'ended'>('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const [formData, setFormData] = useState<SubscriptionFormData>(getInitialFormData());
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [saving, setSaving] = useState(false);
  const [inrRates, setInrRates] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchData();
    fetchInrRates();
  }, []);

  const fetchInrRates = async () => {
    const currenciesToFetch = currencyOptionsBase
      .map((currency) => currency.value)
      .filter((code) => code !== 'INR');

    const fetchRateToInr = async (code: string): Promise<number | null> => {
      try {
        const primaryResponse = await fetch(`https://api.frankfurter.app/latest?from=${code}&to=INR`);
        if (primaryResponse.ok) {
          const primaryData = await primaryResponse.json() as { rates?: Record<string, number> };
          const primaryRate = primaryData?.rates?.INR;
          if (typeof primaryRate === 'number') {
            return primaryRate;
          }
        }
      } catch {
      }

      try {
        const fallbackResponse = await fetch(`https://open.er-api.com/v6/latest/${code}`);
        if (!fallbackResponse.ok) return null;
        const fallbackData = await fallbackResponse.json() as { rates?: Record<string, number> };
        const fallbackRate = fallbackData?.rates?.INR;
        return typeof fallbackRate === 'number' ? fallbackRate : null;
      } catch {
        return null;
      }
    };

    const results = await Promise.all(
      currenciesToFetch.map(async (code) => {
        const rate = await fetchRateToInr(code);
        return typeof rate === 'number' ? { code, rate } : null;
      })
    );

    const rateMap = results.reduce<Record<string, number>>((acc, item) => {
      if (item) {
        acc[item.code] = item.rate;
      }
      return acc;
    }, {});

    setInrRates(rateMap);
  };

  const currencyOptions = currencyOptionsBase.map((currency) => {
    const liveRate = inrRates[currency.value];
    if (currency.value !== 'INR' && typeof liveRate === 'number') {
      return {
        ...currency,
        label: `${currency.label} (1 ${currency.value} = ${liveRate.toFixed(2)} INR today)`,
      };
    }
    return currency;
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ledgerRes, usersRes, settingsRes] = await Promise.all([
        getLedger(),
        getUsers(),
        getSettings(),
      ]);
      
      if (ledgerRes.success) setSubscriptions(ledgerRes.subscriptions);
      if (usersRes.success) {
        setUsers(usersRes.users);
        if (!formData.ownerUserId && usersRes.users.length > 0) {
          setFormData((prev) => ({ ...prev, ownerUserId: usersRes.users[0].id }));
        }
      }
      if (settingsRes.success) {
        setSettings(settingsRes.settings);
        setFormData((prev) => (
          prev.currency === 'INR'
            ? { ...prev, currency: settingsRes.settings.currency || 'INR' }
            : prev
        ));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSubscriptions = subscriptions.filter((sub) => {
    const matchesSearch = sub.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    const matchesCategory = !categoryFilter || sub.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const defaultCurrency = settings?.currency || 'INR';

  const convertAmount = (amount: number, fromCurrency: string, toCurrency: string) => {
    if (!fromCurrency || !toCurrency || fromCurrency === toCurrency) {
      return amount;
    }

    if (fromCurrency === 'INR') {
      const toCurrencyInrRate = inrRates[toCurrency];
      if (typeof toCurrencyInrRate === 'number' && toCurrencyInrRate > 0) {
        return amount / toCurrencyInrRate;
      }
      return amount;
    }

    if (toCurrency === 'INR') {
      const fromCurrencyInrRate = inrRates[fromCurrency];
      if (typeof fromCurrencyInrRate === 'number') {
        return amount * fromCurrencyInrRate;
      }
      return amount;
    }

    const fromCurrencyInrRate = inrRates[fromCurrency];
    const toCurrencyInrRate = inrRates[toCurrency];
    if (
      typeof fromCurrencyInrRate === 'number' &&
      typeof toCurrencyInrRate === 'number' &&
      toCurrencyInrRate > 0
    ) {
      return (amount * fromCurrencyInrRate) / toCurrencyInrRate;
    }

    return amount;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: defaultCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const categories = settings?.categories || ['Streaming', 'Software', 'Utilities', 'Insurance', 'Membership', 'Other'];

  const handleAdd = async () => {
    try {
      setSaving(true);
      const response = await addSubscription(formData);
      if (response.success && response.subscription) {
        setSubscriptions([...subscriptions, response.subscription]);
        setShowAddModal(false);
        setFormData(getInitialFormData(settings?.currency || 'INR'));
      }
    } catch (error) {
      console.error('Failed to add subscription:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedSubscription) return;
    
    try {
      setSaving(true);
      const response = await updateSubscription(selectedSubscription.id, formData);
      if (response.success && response.subscription) {
        setSubscriptions(subscriptions.map((s) =>
          s.id === selectedSubscription.id ? response.subscription! : s
        ));
        setShowEditModal(false);
        setSelectedSubscription(null);
        setFormData(getInitialFormData(settings?.currency || 'INR'));
      }
    } catch (error) {
      console.error('Failed to update subscription:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEnd = async () => {
    if (!selectedSubscription) return;
    
    try {
      setSaving(true);
      const response = await endSubscription(selectedSubscription.id, endDate);
      if (response.success && response.subscription) {
        setSubscriptions(subscriptions.map((s) =>
          s.id === selectedSubscription.id ? response.subscription! : s
        ));
        setShowEndModal(false);
        setSelectedSubscription(null);
      }
    } catch (error) {
      console.error('Failed to end subscription:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSubscription) return;
    
    try {
      setSaving(true);
      const response = await deleteSubscription(selectedSubscription.id);
      if (response.success) {
        setSubscriptions(subscriptions.filter((s) => s.id !== selectedSubscription.id));
        setShowDeleteModal(false);
        setSelectedSubscription(null);
      }
    } catch (error) {
      console.error('Failed to delete subscription:', error);
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (sub: Subscription) => {
    setSelectedSubscription(sub);
    setFormData({
      name: sub.name,
      amount: sub.amount,
      currency: sub.currency,
      billingDay: sub.billingDay,
      billingCycle: sub.billingCycle,
      startDate: sub.startDate,
      category: sub.category,
      notes: sub.notes,
      ownerUserId: sub.ownerUserId,
    });
    setShowEditModal(true);
  };

  const openEndModal = (sub: Subscription) => {
    setSelectedSubscription(sub);
    setEndDate(format(new Date(), 'yyyy-MM-dd'));
    setShowEndModal(true);
  };

  const openDeleteModal = (sub: Subscription) => {
    setSelectedSubscription(sub);
    setShowDeleteModal(true);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Subscriptions</h1>
          <p className="text-dark-400 mt-1">Manage your recurring subscriptions</p>
        </div>
        
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="w-4 h-4" />
          Add Subscription
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
            <Input
              type="text"
              placeholder="Search subscriptions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'ended')}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'ended', label: 'Ended' },
            ]}
            className="w-full sm:w-40"
          />
          
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            options={[
              { value: '', label: 'All Categories' },
              ...categories.map((c) => ({ value: c, label: c })),
            ]}
            className="w-full sm:w-40"
          />
        </CardContent>
      </Card>

      {/* Subscriptions Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Subscription
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Billing
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700">
                {filteredSubscriptions.length > 0 ? (
                  filteredSubscriptions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-dark-700/50 transition-colors">
                      <td className="px-4 sm:px-6 py-4">
                        <div className="text-white font-medium">{sub.name}</div>
                        <div className="text-dark-400 text-sm">
                          Started {format(new Date(sub.startDate), 'MMM d, yyyy')}
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-dark-300">{sub.category || 'Other'}</td>
                      <td className="px-4 sm:px-6 py-4">
                        <div className="text-dark-300">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            sub.billingCycle === 'monthly' 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {sub.billingCycle}
                          </span>
                        </div>
                        <div className="text-dark-400 text-sm mt-1">Day {sub.billingDay}</div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-white font-medium">
                        {formatCurrency(convertAmount(sub.amount, sub.currency, defaultCurrency))}
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          sub.status === 'active'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {sub.status}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(sub)}
                            className="p-2 text-dark-400 hover:text-primary-400 hover:bg-dark-700 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {sub.status === 'active' && (
                            <button
                              onClick={() => openEndModal(sub)}
                              className="p-2 text-dark-400 hover:text-orange-400 hover:bg-dark-700 rounded-lg transition-colors"
                              title="End subscription"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => openDeleteModal(sub)}
                            className="p-2 text-dark-400 hover:text-red-400 hover:bg-dark-700 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 sm:px-6 py-8 text-center text-dark-400">
                      No subscriptions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setFormData(getInitialFormData(settings?.currency || 'INR'));
        }}
        title="Add Subscription"
        size="lg"
      >
        <SubscriptionForm
          formData={formData}
          setFormData={setFormData}
          categories={categories}
          users={users}
          currencyOptions={currencyOptions}
          ownerLabel={isRootAdmin ? 'Assign to Family Account' : 'Assign to Family Member'}
          onSubmit={handleAdd}
          onCancel={() => {
            setShowAddModal(false);
            setFormData(getInitialFormData(settings?.currency || 'INR'));
          }}
          saving={saving}
          submitLabel="Add Subscription"
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedSubscription(null);
          setFormData(getInitialFormData(settings?.currency || 'INR'));
        }}
        title="Edit Subscription"
        size="lg"
      >
        <SubscriptionForm
          formData={formData}
          setFormData={setFormData}
          categories={categories}
          users={users}
          currencyOptions={currencyOptions}
          ownerLabel={isRootAdmin ? 'Assign to Family Account' : 'Assign to Family Member'}
          onSubmit={handleUpdate}
          onCancel={() => {
            setShowEditModal(false);
            setSelectedSubscription(null);
            setFormData(getInitialFormData(settings?.currency || 'INR'));
          }}
          saving={saving}
          submitLabel="Save Changes"
        />
      </Modal>

      {/* End Modal */}
      <Modal
        isOpen={showEndModal}
        onClose={() => {
          setShowEndModal(false);
          setSelectedSubscription(null);
        }}
        title="End Subscription"
      >
        <div className="space-y-4">
          <p className="text-dark-300">
            Are you sure you want to end <span className="text-white font-medium">{selectedSubscription?.name}</span>?
          </p>
          <Input
            type="date"
            label="End Date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <div className="flex gap-3 justify-end mt-6">
            <Button
              variant="secondary"
              onClick={() => {
                setShowEndModal(false);
                setSelectedSubscription(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleEnd} disabled={saving}>
              {saving ? <LoadingSpinner size="sm" /> : 'End Subscription'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSubscription(null);
        }}
        title="Delete Subscription"
      >
        <div className="space-y-4">
          <p className="text-dark-300">
            Are you sure you want to permanently delete <span className="text-white font-medium">{selectedSubscription?.name}</span>? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-end mt-6">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedSubscription(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={saving}>
              {saving ? <LoadingSpinner size="sm" /> : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Subscription Form Component
interface SubscriptionFormProps {
  formData: SubscriptionFormData;
  setFormData: (data: SubscriptionFormData) => void;
  categories: string[];
  users: User[];
  currencyOptions: { value: string; label: string }[];
  ownerLabel: string;
  onSubmit: () => void;
  onCancel: () => void;
  saving: boolean;
  submitLabel: string;
}

function SubscriptionForm({
  formData,
  setFormData,
  categories,
  users,
  currencyOptions,
  ownerLabel,
  onSubmit,
  onCancel,
  saving,
  submitLabel,
}: SubscriptionFormProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Netflix"
          required
        />
        
        <Input
          label="Amount"
          type="number"
          value={formData.amount}
          onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
          placeholder="0"
          required
        />
        
        <Select
          label="Category"
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          options={[
            { value: '', label: 'Select category' },
            ...categories.map((c) => ({ value: c, label: c })),
          ]}
        />
        
        <Select
          label="Billing Cycle"
          value={formData.billingCycle}
          onChange={(e) => setFormData({ ...formData, billingCycle: e.target.value as 'monthly' | 'yearly' })}
          options={[
            { value: 'monthly', label: 'Monthly' },
            { value: 'yearly', label: 'Yearly' },
          ]}
        />
        
        <Input
          label="Billing Day"
          type="number"
          min={1}
          max={28}
          value={formData.billingDay}
          onChange={(e) => setFormData({ ...formData, billingDay: Number(e.target.value) })}
        />
        
        <Input
          label="Start Date"
          type="date"
          value={formData.startDate}
          onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
        />
        
        <Select
          label={ownerLabel}
          value={formData.ownerUserId}
          onChange={(e) => setFormData({ ...formData, ownerUserId: e.target.value })}
          options={[
            { value: '', label: 'Unassigned' },
            ...users.map((u) => ({ value: u.id, label: u.name })),
          ]}
        />
        
        <Select
          label="Currency"
          value={formData.currency}
          onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
          options={currencyOptions}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-dark-300 mb-1">Notes</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="w-full px-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          rows={3}
          placeholder="Optional notes..."
        />
      </div>
      
      <div className="flex gap-3 justify-end mt-6">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={saving || !formData.name}>
          {saving ? <LoadingSpinner size="sm" /> : submitLabel}
        </Button>
      </div>
    </div>
  );
}
